import React, { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { setProjectMemberAcl } from '@/services/projects';
import { Button, ButtonBar, Dialog } from '@ygtechnologies/rubus-ui';
import ProjectPermissionsEditor from './ProjectPermissionsEditor';
import { ProjectMemberDetails, ProjectMemberPermissions } from '@/types';

interface ProjectPermissionsEditDialogProps {
    show: boolean;
    memberDetails: ProjectMemberDetails | null;
    onAccept(newPermissions: ProjectMemberPermissions): void;
    onClose(): void;
    projectId: number;
}

const ProjectPermissionsEditDialog: React.FC<ProjectPermissionsEditDialogProps> = ({
    memberDetails,
    show,
    onAccept,
    onClose,
    projectId,
}) => {
    const [saving, setSaving] = useState(false);
    const [newPermissions, setNewPermissions] = useState<ProjectMemberPermissions | null>(null);

    const handleOpen = () => {
        setNewPermissions(
            memberDetails
                ? {
                      projectPermissions: memberDetails.projectPermissions,
                      categoryPermissions: memberDetails.categoryPermissions,
                      allowedProjectFeeSetIds: memberDetails.allowedProjectFeeSetIds,
                      isAdministrator: memberDetails.isAdministrator,
                  }
                : {
                      projectPermissions: [],
                      categoryPermissions: [],
                      allowedProjectFeeSetIds: [],
                      isAdministrator: false,
                  }
        );
        setSaving(false);
    };

    const handleClosed = () => {
        setNewPermissions(null);
    };

    const handleAcceptClick = async () => {
        if (!memberDetails || !newPermissions) {
            return;
        }

        setSaving(true);

        try {
            await setProjectMemberAcl(memberDetails.projectMemberId, newPermissions);
            triggerHandler(onAccept, newPermissions);
            triggerHandler(onClose);
        } finally {
            setSaving(false);
        }
    };

    const handleCancelClick = () => {
        triggerHandler(onClose);
    };
    return (
        <Dialog
            open={show}
            onOpenChange={(open) => {
                if (!open) {
                    handleCancelClick();
                    handleClosed();
                }
            }}
            onOpen={handleOpen}>
            <Dialog.Content contentWidth="lg">
                <Dialog.Header>
                    <Trans>Edit {memberDetails ? memberDetails.displayName : ''} permissions</Trans>
                </Dialog.Header>
                {newPermissions && (
                    <ProjectPermissionsEditor
                        projectId={projectId}
                        value={newPermissions}
                        onChange={(newValue) => setNewPermissions(newValue)}
                    />
                )}
                <ButtonBar>
                    <ButtonBar.Right>
                        <Button variant="tertiary" onClick={handleCancelClick} disabled={saving}>
                            <Trans>Cancel</Trans>
                        </Button>
                        <Button variant="primary" onClick={handleAcceptClick} loading={saving}>
                            <Trans>Save</Trans>
                        </Button>
                    </ButtonBar.Right>
                </ButtonBar>
            </Dialog.Content>
        </Dialog>
    );
};

export default ProjectPermissionsEditDialog;
